import React, { useEffect, useState } from "react";
import { ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

const RISK_STYLE = { NORMAL: "text-emerald-400", REVIEW: "text-indigo-300", BLOCKED: "text-rose-400" };

export default function Fraud() {
  const [d, setD] = useState(null);
  const [state, setState] = useState("");
  const [entity, setEntity] = useState("");
  const [busy, setBusy] = useState("");

  const load = async () => {
    const params = {};
    if (state) params.state = state; if (entity) params.entity = entity;
    try { const { data } = await api.get("/admin/fraud", { params }); setD(data); }
    catch { toast.error("Could not load fraud data"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [state, entity]);

  const act = async (f, action) => {
    let reason = "";
    if (action !== "RESOLVE") { reason = prompt(`Reason for ${action}?`) || ""; }
    setBusy(f.entity_id);
    try { await api.post("/admin/fraud/action", { entity: f.entity, entity_id: f.entity_id, action, reason }); toast.success(`${action} done`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-fraud">
      <div className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-rose-400" /><h1 className="font-heading text-2xl font-bold">Fraud &amp; risk</h1></div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card label="Under review" value={d.counts.REVIEW || 0} testid="fraud-review-count" />
        <Card label="Blocked" value={d.counts.BLOCKED || 0} danger testid="fraud-blocked-count" />
        <Card label="High-risk customers" value={d.high_risk_customers} testid="fraud-hr-customers" />
        <Card label="Txns under review" value={d.under_review_txns} testid="fraud-txns-review" />
      </div>

      <div className="flex flex-wrap gap-2">
        <select data-testid="fraud-filter-state" value={state} onChange={(e) => setState(e.target.value)} className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm">
          <option value="">All states</option><option value="REVIEW">Review</option><option value="BLOCKED">Blocked</option><option value="NORMAL">Normal</option>
        </select>
        <select data-testid="fraud-filter-entity" value={entity} onChange={(e) => setEntity(e.target.value)} className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm">
          <option value="">All entities</option><option value="customer">Customers</option><option value="merchant">Merchants</option>
        </select>
      </div>

      <div className="ledger-card overflow-hidden rounded-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="fraud-table">
            <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="p-4 text-left">Entity</th><th className="p-4 text-left">ID</th><th className="p-4 text-left">Risk</th><th className="p-4 text-left">Reason</th><th className="p-4 text-left">Date</th><th className="p-4"></th></tr>
            </thead>
            <tbody>
              {d.flags.map((f) => (
                <tr key={`${f.entity}-${f.entity_id}`} data-testid={`fraud-row-${f.entity_id}`} className="border-t border-slate-800/60">
                  <td className="p-4 capitalize">{f.entity}</td>
                  <td className="p-4 font-mono text-xs text-slate-400">{f.entity_id}</td>
                  <td className={`p-4 font-mono text-xs ${RISK_STYLE[f.risk_state]}`}>{f.risk_state}</td>
                  <td className="p-4 text-slate-300">{f.reason}</td>
                  <td className="p-4 text-slate-400">{f.created_at ? new Date(f.created_at).toLocaleString("en-IN") : "—"}</td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      {f.risk_state !== "NORMAL" && <Btn testid={`fraud-resolve-${f.entity_id}`} label="Resolve" onClick={() => act(f, "RESOLVE")} disabled={busy === f.entity_id} className="border-emerald-500/40 text-emerald-300" />}
                      {f.risk_state !== "REVIEW" && <Btn testid={`fraud-review-${f.entity_id}`} label="Review" onClick={() => act(f, "REVIEW")} disabled={busy === f.entity_id} className="border-indigo-500/40 text-indigo-300" />}
                      {f.risk_state !== "BLOCKED" && <Btn testid={`fraud-block-${f.entity_id}`} label="Block" onClick={() => act(f, "BLOCK")} disabled={busy === f.entity_id} className="border-rose-500/40 text-rose-300" />}
                    </div>
                  </td>
                </tr>
              ))}
              {d.flags.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No fraud flags.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Btn({ label, onClick, disabled, className, testid }) {
  return <button data-testid={testid} onClick={onClick} disabled={disabled} className={`rounded-full border px-3 py-1 text-xs font-semibold hover:bg-white/5 disabled:opacity-50 ${className}`}>{label}</button>;
}
function Card({ label, value, danger, testid }) {
  return <div className={`ledger-card rounded-2xl p-5 ${danger && value > 0 ? "border-rose-500/30" : ""}`} data-testid={testid}><p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</p><p className={`mt-2 font-heading text-2xl font-extrabold ${danger && value > 0 ? "text-rose-400" : "text-slate-100"}`}>{value}</p></div>;
}
